/**
 * Building image URLs: the tenant's media host, and the same-origin proxy.
 *
 * Every failure in the lp-image-proxy-failures alarm (2026-08-06) came from a
 * URL assembled by hand, and the same three mistakes recurred across six call
 * sites:
 *
 *  - **Double-wrap.** `my-listings` hands the image editor a URL it has
 *    already routed through the proxy; the editor's canvas-taint retry then
 *    wrapped it a second time, producing `image-proxy?url=<image-proxy url>`.
 *    Those can never resolve — the proxy fetches itself.
 *
 *  - **Empty sources.** A listing with no photo produced `${base}/`, a request
 *    for the bucket root, which answers 403. 4,164 of New Mexico's 5,927
 *    published listings carry no image, so this was the bulk of the noise.
 *
 *  - **The wrong tenant's bucket.** `environment.cloudFrontBase` reads
 *    `window.__env.CLOUDFRONT_BASE` — which nothing in this repo ever sets —
 *    so it is always New Mexico's media host, in a single build that serves
 *    every tenant. A Cleveland listing was asking New Mexico for its photo.
 *
 * The rule these encode: an unknown media host yields an empty string, never
 * a guess. A missing image is a placeholder; another tenant's bucket is a
 * bug, and a silent one, because a 403 looks the same as "no photo yet".
 */

/** The shape of the site record these helpers need — structural, so callers can pass their own. */
export interface MediaHostSite {
  ext?: { CloudFront?: unknown } | null;
  cloudFrontDomain?: string | null;
  s3Bucket?: string | null;
}

function withScheme(host: string): string {
  const trimmed = host.trim().replace(/\/+$/, '');
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

/** True for sources that are already fully resolved and must be handed back untouched. */
function isSelfContained(value: string): boolean {
  return (
    /^https?:\/\//i.test(value) ||
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    value.startsWith('/assets/')
  );
}

/**
 * Resolve the media host for the CURRENT tenant, in the same priority
 * `ExternalApiService.initializeSite` uses: `Ext.CloudFront`, then
 * `CloudFrontDomain`, then the tenant's S3 bucket directly.
 *
 * The S3 rung is not decorative — Cleveland has no media distribution, and
 * its bucket URL is the only host that serves its photos.
 *
 * Returns '' when the site is unknown or carries none of the three. That is
 * deliberate: the previous fallback was a hardcoded New Mexico host, which
 * served NM's bucket to whichever tenant happened to be asking.
 */
export function resolveMediaBase(site: MediaHostSite | null | undefined): string {
  const fromExt = String((site?.ext as { CloudFront?: unknown } | null)?.CloudFront ?? '').trim();
  if (fromExt) return withScheme(fromExt);

  const fromDomain = String(site?.cloudFrontDomain ?? '').trim();
  if (fromDomain) return withScheme(fromDomain);

  const fromBucket = String(site?.s3Bucket ?? '').trim();
  if (fromBucket) return withScheme(`${fromBucket}.s3.amazonaws.com`);

  return '';
}

/**
 * Join a stored path onto the tenant's media host.
 *
 * Empty path in, empty string out — the caller renders a placeholder instead
 * of firing a request that can only 403. An already-absolute source (http(s),
 * data:, blob:, /assets/) is returned untouched.
 */
export function mediaUrl(base: string | null | undefined, path: string | null | undefined): string {
  const p = String(path ?? '').trim();
  if (!p) return '';
  if (isSelfContained(p)) return p;

  const b = String(base ?? '').trim().replace(/\/+$/, '');
  if (!b) return '';

  return `${b}/${p.replace(/^\/+/, '')}`;
}

/** True when this URL already points at the image proxy. */
export function isProxiedUrl(url: string | null | undefined): boolean {
  const value = String(url ?? '');
  return value.includes('/image-proxy') && /[?&]url=/.test(value);
}

/**
 * Route a URL through the same-origin image proxy, which exists so that
 * CORS-less tenant buckets don't taint the editor canvas.
 *
 * Idempotent: a URL that is already proxied comes back unchanged, so a retry
 * layered on top of a caller that pre-proxied can no longer build
 * `image-proxy?url=<image-proxy url>`. Empty in, empty out.
 */
export function proxyImageUrl(apiUrl: string | null | undefined, url: string | null | undefined): string {
  const raw = String(url ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
  if (isProxiedUrl(raw)) return raw;

  const base = String(apiUrl ?? '').trim().replace(/\/+$/, '');
  if (!base) return raw;

  return `${base}/image-proxy?url=${encodeURIComponent(raw)}`;
}
